import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { IWorkingDay } from '@/modules/workingDay/types';
import axiosClient from '@/shared/lib/axios';
import { IGeneral } from '@/shared/types';

// *********************************************** CREATE TECHNICAL OPERATION **********************************
export const createTechnicalOperation = async (
  params: IWorkingDay.IOperationFormSchema
): Promise<IWorkingDay.ICreateOperationResponse> => {
  const { data } = await axiosClient.post(
    '/company/portalInsights/operations/job-instance',
    params
  );
  return data;
};
export const useCreateTechnicalOperation = () => {
  const queryClient = useQueryClient();

  return useMutation(['createTechnicalOperation'], createTechnicalOperation, {
    onSuccess: () => {
      queryClient.invalidateQueries(['getWorkingDay']);
      queryClient.invalidateQueries(['getOnboardingStatus']);
    },
  });
};
// *********************************************** GET TARGET LIST **********************************
export const getTargetList = async (
  params: IGeneral.IPaginationParamsSchema
): Promise<IWorkingDay.ITargetListResponse> => {
  const { data } = await axiosClient.get<IWorkingDay.IGetTargetListResponse>(
    '/company/portalInsights/operations/target-durations',
    { params }
  );
  return {
    items: data.targetDurations,
    currentPage: data.currentPage,
    totalItems: data.totalItems,
    totalPages: data.totalPages,
    nextPage:
      params?.pageNumber < data.totalPages ? params?.pageNumber + 1 : undefined,
  };
};
export const useGetTargetList = () => {
  return useInfiniteQuery({
    queryKey: ['getTargetList'],
    queryFn: ({ pageParam }) =>
      getTargetList({
        pageSize: 10,
        pageNumber: pageParam ?? 1,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });
};
// *********************************************** GET SUPERVISORS LIST **********************************
export const getSupervisorsList = async ({
  params,
  entityId,
}: {
  params: IGeneral.IPaginationParamsSchema;
  entityId?: number;
}): Promise<IWorkingDay.ISupervisorsResponseSchecma> => {
  const { data } =
    await axiosClient.get<IWorkingDay.IGetSupervisorsResponseSchecma>(
      `/company/portalInsights/operations/supervisors/${entityId}`,
      { params }
    );
  const totalPages = Math.ceil(data.totalCount / 10);

  return {
    currentPage: data.currentPage ?? 1,
    items: data.supervisors,
    totalItems: data.totalCount,
    totalPages,
    nextPage:
      params?.pageNumber < totalPages ? params?.pageNumber + 1 : undefined,
  };
};
export const useGetSupervisorsList = (entityId?: number) => {
  return useInfiniteQuery({
    queryKey: ['getSupervisorsList'],
    queryFn: ({ pageParam }) =>
      getSupervisorsList({
        params: {
          pageSize: 10,
          pageNumber: pageParam ?? 1,
        },
        entityId,
      }),
    enabled: !!entityId,
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });
};
// *********************************************** GET FIELDS LIST **********************************
export const getFieldsList = async ({
  params,
  entityId,
}: {
  params: IGeneral.IPaginationParamsSchema;
  entityId?: number;
}): Promise<IWorkingDay.IFieldsResponseSchecma> => {
  const { data } = await axiosClient.get<IWorkingDay.IGetFieldsResponseSchecma>(
    `/company/portalInsights/operations/fields/${entityId}`,
    { params }
  );
  const totalPages = Math.ceil(data.totalCount / 10) ?? 1;

  return {
    currentPage: data.currentPage ?? 1,
    items: data.entityFields,
    totalItems: data.totalCount,
    totalPages,
    nextPage:
      params?.pageNumber < totalPages ? params?.pageNumber + 1 : undefined,
  };
};
export const useGetFieldsList = (entityId?: number) => {
  return useInfiniteQuery({
    queryKey: ['getFieldsList', entityId],
    queryFn: ({ pageParam }) =>
      getFieldsList({
        params: {
          pageSize: 10,
          pageNumber: pageParam ?? 1,
        },
        entityId,
      }),
    enabled: !!entityId,
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });
};
// *********************************************** GET JOBS LIST **********************************
export const getJobsList = async ({
  cropId,
  entityId,
}: {
  entityId?: number;
  cropId?: number;
}): Promise<IWorkingDay.IJobsResponseSchecma> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/operations/jobs/${entityId}/${cropId}`
  );

  return data;
};
export const useGetJobsList = ({
  cropId,
  entityId,
}: {
  entityId?: number;
  cropId?: number;
}) => {
  return useQuery({
    queryKey: ['getTechJobsList', cropId, entityId],
    queryFn: () =>
      getJobsList({
        entityId,
        cropId,
      }),
    enabled: !!cropId && !!entityId,
  });
};

// *********************************************** GET MEASUREMENT UNITS LIST **********************************
export const getMeasurementUnitsList = async (
  entityId?: number
): Promise<IWorkingDay.IUnitsResponseSchecma> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/operations/measurement-units/${entityId}`
  );
  return data;
};
export const useGetMeasurementUnitsList = (entityId?: number) => {
  return useQuery(
    ['getMeasurement-unitsList'],
    () => getMeasurementUnitsList(entityId),
    { enabled: !!entityId }
  );
};

// *********************************************** working day **********************************
export const getWorkingDay = async (
  params: IWorkingDay.IGetWorkingDaySchema
): Promise<{
  entityCrops: IWorkingDay.IEntityCrop[];
  jobInstances: IWorkingDay.IJobInstance[];
  totalCount: number;
  jobInstancesTotalCount: number;
}> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/working-day',
    {
      params: params,
    }
  );
  return data;
};
export const useGetWorkingDay = (params: IWorkingDay.IGetWorkingDaySchema) => {
  return useQuery(['getWorkingDay', params], () => getWorkingDay(params), {
    enabled: Boolean(
      (params.entityId === 0 ? true : params.entityId) && params.date
    ),
  });
};

// *********************************************** instance details **********************************

export const getJobInstance = async (
  params: IWorkingDay.IGetJobInstanceSchema
): Promise<{
  jobInstance: IWorkingDay.IJobInstance;
  suppliers: IWorkingDay.IJobInstanceSupplier[];
}> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/job-instance',
    {
      params: params,
    }
  );
  return data;
};
export const useGetJobInstance = (
  params: IWorkingDay.IGetJobInstanceSchema
) => {
  return useQuery(['getJobInstance', params], () => getJobInstance(params), {
    enabled: Boolean(params.entityId && params.jobInstanceId),
  });
};

// getIncidentReport

export const getIncidentReport = async (
  params: IWorkingDay.IGetIncidentReportSchema
): Promise<IWorkingDay.IGetIncidentReportData> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/incident-report',
    {
      params: params,
    }
  );
  return data;
};
export const useGetIncidentReport = (
  params: IWorkingDay.IGetIncidentReportSchema
) => {
  return useQuery(
    ['getIncidentReport', params],
    () => getIncidentReport(params),
    {
      enabled: Boolean(params.entityId && params.incidentReportId),
    }
  );
};
